/* Her locked palette, as the thirteen colour tokens a site builder wants.
 *
 * `readBrandSkin` (brandSkin.js) reads six roles out of her locked My Voice &
 * Branding — a light base, a dark base, a brand colour, an accent, and the two
 * text colours. A deck needs exactly those six. A WEBSITE wants thirteen:
 * background, foreground, surface, surfaceForeground, primary,
 * primaryForeground, secondary, muted, mutedForeground, accent,
 * accentForeground, border, ring (docs/site-brief-spec.md §4).
 *
 * The other seven are derived, not invented — which we know because the
 * builder showed its own working. Asked for a starter brief it returned a
 * `_previewPalette` mapping her seven hexes into all thirteen, and the rule it
 * used is the obvious one:
 *
 *   darkest        → background        (in dark mode)
 *   lightest       → foreground
 *   second-darkest → surface, muted, border
 *   brand          → primary
 *   brightest      → accent, ring
 *   every *Foreground → whichever of light/dark reads against its pair
 *
 * So this implements that rule rather than a taste of its own. Where her
 * palette does not carry a fourth or fifth colour, a shade of one she has is
 * used — a surface has to differ from the background it sits on or the cards
 * disappear, and that is arithmetic rather than design.
 *
 * TWO MODES, because a palette is not a scheme. Her brand holds a cream AND a
 * near-black; which is the page and which is the ink is a decision about the
 * site, not a fact about the brand. Dark is what the builder inferred from her
 * colours and what she liked; light is the same palette the other way up.
 *
 * NOTHING HERE READS PROSE. It takes the skin brandSkin already read, so the
 * one tolerant reader of her locked text stays in one place — see the note at
 * the top of brandSkin.js about that seam failing silently.
 */

import { contrast, luminance, shade } from "./deckLayout";

const hex = (h) => (h ? `#${String(h).replace(/^#/, "").toUpperCase()}` : "");

/* Text that can actually be read on a given colour. Not a preference — the
   pair with more contrast wins, and if both are poor the better one still
   wins, because a site with unreadable buttons is worse than one that is
   slightly off-brand. */
function readableOn(bg, light, dark) {
  const l = hex(light) || "#FFFFFF";
  const d = hex(dark) || "#111111";
  return contrast(bg.replace("#", ""), l.replace("#", "")) >= contrast(bg.replace("#", ""), d.replace("#", "")) ? l : d;
}

/**
 * @param skin  what readBrandSkin returned — {bgPrimary, bgDark, brandPrimary,
 *              brandAccent, textOnLight, textOnDark}, any of which may be absent
 * @param mode  "dark" | "light"
 * @returns { tokens, notes } — thirteen hex strings, and what was assumed
 */
export function siteTokens(skin = {}, { mode = "dark" } = {}) {
  const notes = [];
  const say = (n) => notes.push(n);

  /* Sensible only where her palette is silent. Every default that survives to
     the output is named in `notes`, because a colour nobody chose is exactly
     the kind of thing that looks deliberate on a client's page. */
  const light = hex(skin.bgPrimary) || (say("No light colour in your palette — used white."), "#FFFFFF");
  const dark = hex(skin.bgDark) || (say("No dark colour in your palette — used near-black."), "#111111");
  const brand = hex(skin.brandPrimary) || (say("No brand colour in your palette — used your accent."), hex(skin.brandAccent)) || (say("No brand or accent colour — used your dark."), dark);
  const accent = hex(skin.brandAccent) || brand;
  const onLight = hex(skin.textOnLight) || shadeHex(dark, 0);
  const onDark = hex(skin.textOnDark) || light;

  const isDark = mode === "dark";
  const background = isDark ? dark : light;
  const foreground = isDark ? onDark : onLight;

  /* A surface has to be visibly different from the page or cards vanish. Her
     palette rarely names one, so it is the background lifted (dark mode) or
     dropped (light mode) a little — the same move the builder made when it put
     her indigo on her near-black. */
  const surface = isDark ? shadeHex(background, -0.14) : shadeHex(background, 0.05);
  const border = isDark ? shadeHex(background, -0.22) : shadeHex(background, 0.1);
  const muted = surface;

  const tokens = {
    background,
    foreground,
    surface,
    surfaceForeground: readableOn(surface, onDark, onLight),
    primary: brand,
    primaryForeground: readableOn(brand, onDark, onLight),
    /* A second brand voice if she has one; a quieter version of the first if
       not. Never the accent — that is the loud one, and two loud colours with
       different jobs is how a page stops having a hierarchy. */
    secondary: hex(skin.brandSecondary) || shadeHex(brand, isDark ? 0.25 : -0.25),
    muted,
    mutedForeground: dim(readableOn(muted, onDark, onLight), muted),
    accent,
    accentForeground: readableOn(accent, onDark, onLight),
    border,
    ring: accent,
  };

  if (!skin.brandAccent && skin.brandPrimary) say("No accent colour — your brand colour is doing both jobs.");
  if (tokens.accent === tokens.primary) say("Accent and primary are the same colour, so highlights will not stand apart.");

  /* WHERE HER OWN COLOURS CANNOT CARRY SMALL TEXT, SAY SO — do not fix it.
     Her brand teal with her off-white is about 4.1:1, under the 4.5 that small
     text needs. Nudging the text to pure white would pass and would also stop
     being her palette, which is not a trade this file gets to make on her
     behalf. It is fine on a big button and wrong in body copy, and that is a
     judgement she can make once she has been told.
     Same class of thing as the Send button flagged at 3.1:1 in the chat
     repalette — worth reporting every time rather than discovering later. */
  const WHERE = {
    foreground: ["background", "your body text on the page"],
    surfaceForeground: ["surface", "text on cards"],
    primaryForeground: ["primary", "text on your brand-colour buttons"],
    accentForeground: ["accent", "text on your accent"],
    mutedForeground: ["muted", "your quieter text"],
  };
  for (const [fg, [bg, what]] of Object.entries(WHERE)) {
    const r = contrast(tokens[fg].replace("#", ""), tokens[bg].replace("#", ""));
    if (r < 4.5) say(`${what} reads at ${Math.round(r * 10) / 10}:1 — under the 4.5 small text needs. Fine at large sizes; hard to read in a paragraph.`);
  }

  return { tokens, notes };
}

/* `shade` returns bare hex; everything here speaks in #RRGGBB. */
function shadeHex(h, amount) {
  return `#${shade(String(h).replace(/^#/, ""), amount)}`;
}

/* Muted text is quieter than body text but still has to be READ. Pulled TOWARD
   its own background until it is calm, and no further than 4.5:1 — the line
   where small text stops being accessible.
   Toward the background is the whole idea, and getting the direction wrong
   does not look broken, it looks BOLD: the first version moved it away, and
   muted text on the dark card came out #F8F7F5, brighter than the body copy
   beside it. Caught by printing her real palette rather than by a test, which
   is why it is printed. */
function dim(colour, on) {
  const bgIsLight = luminance(on.replace("#", "")) > 0.5;
  let out = colour;
  for (let i = 0; i < 6; i += 1) {
    // Toward white when the background is light, toward black when it is dark.
    const next = shadeHex(out, bgIsLight ? -0.12 : 0.12);
    if (contrast(next.replace("#", ""), on.replace("#", "")) < 4.5) break;
    out = next;
  }
  return out;
}
